import { supabase } from '../lib/supabase';

export const roiCalculatorService = {
  // Calculate ROI metrics using database function
  async calculateROI(calculationData) {
    try {
      const { data, error } = await supabase?.rpc('calculate_roi_metrics', {
        property_price_param: calculationData?.propertyPrice,
        down_payment_percentage_param: calculationData?.downPaymentPercentage,
        loan_interest_rate_param: calculationData?.loanInterestRate,
        loan_term_years_param: calculationData?.loanTermYears,
        expected_rental_income_param: calculationData?.expectedRentalIncome,
        monthly_expenses_param: calculationData?.monthlyExpenses,
        annual_appreciation_rate_param: calculationData?.annualAppreciationRate
      });

      if (error) {
        console.error('Erreur lors du calcul ROI. Votre projet Supabase pourrait être en pause. Veuillez vérifier votre tableau de bord Supabase.');
        return null;
      }

      return data;
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        console.error('Impossible de se connecter au service de calcul. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
        return null;
      }
      
      console.error('Erreur lors du calcul ROI');
      console.error('Erreur JS lors du calcul ROI:', error);
      return null;
    }
  },

  // Save ROI calculation
  async saveCalculation(calculationData) {
    try {
      const { data, error } = await supabase?.from('roi_calculations')?.insert([{
          calculation_name: calculationData?.calculationName,
          property_id: calculationData?.propertyId,
          property_price: calculationData?.propertyPrice,
          down_payment_percentage: calculationData?.downPaymentPercentage,
          loan_amount: calculationData?.loanAmount,
          loan_interest_rate: calculationData?.loanInterestRate,
          loan_term_years: calculationData?.loanTermYears,
          expected_rental_income: calculationData?.expectedRentalIncome,
          monthly_expenses: calculationData?.monthlyExpenses,
          annual_appreciation_rate: calculationData?.annualAppreciationRate,
          property_tax_rate: calculationData?.propertyTaxRate,
          insurance_cost: calculationData?.insuranceCost,
          maintenance_cost: calculationData?.maintenanceCost,
          cash_on_cash_return: calculationData?.cashOnCashReturn,
          cap_rate: calculationData?.capRate,
          total_roi: calculationData?.totalROI,
          break_even_months: calculationData?.breakEvenMonths,
          yearly_projections: calculationData?.yearlyProjections,
          status: calculationData?.status || 'saved',
          user_id: calculationData?.userId
        }])?.select()?.single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        throw new Error('Impossible de se connecter à la base de données. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
      }
      
      console.error('Erreur JS lors de la sauvegarde:', error);
      throw error;
    }
  },

  // Get user's saved calculations
  async getUserCalculations(userId) {
    try {
      const { data, error } = await supabase?.from('roi_calculations')?.select(`*,property:properties(id, title, address, commune:communes(name))`)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
        throw new Error('Impossible de se connecter à la base de données. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
      }
      
      console.error('Erreur JS lors de la récupération des calculs:', error);
      throw error;
    }
  },

  // Get a specific calculation
  async getCalculation(calculationId) {
    try {
      const { data, error } = await supabase?.from('roi_calculations')?.select(`*,property:properties(id, title, address, price, commune:communes(name))`)?.eq('id', calculationId)?.single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
        throw new Error('Impossible de se connecter à la base de données. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
      }
      
      console.error('Erreur JS lors de la récupération du calcul:', error);
      throw error;
    }
  },

  // Update calculation
  async updateCalculation(calculationId, updates) {
    try {
      const { data, error } = await supabase?.from('roi_calculations')?.update(updates)?.eq('id', calculationId)?.select()?.single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
        throw new Error('Impossible de se connecter à la base de données. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
      }
      
      console.error('Erreur JS lors de la mise à jour du calcul:', error);
      throw error;
    }
  },

  // Delete calculation
  async deleteCalculation(calculationId) {
    try {
      const { error } = await supabase?.from('roi_calculations')?.delete()?.eq('id', calculationId);

      if (error) {
        throw error;
      }

      return true;
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
        throw new Error('Impossible de se connecter à la base de données. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
      }
      
      console.error('Erreur JS lors de la suppression du calcul:', error);
      throw error;
    }
  },

  // Get ROI calculation for a specific property
  async getPropertyROI(propertyId) {
    try {
      const { data, error } = await supabase?.from('roi_calculations')?.select('*')?.eq('property_id', propertyId)?.order('created_at', { ascending: false })?.limit(1)?.single();

      if (error && error?.code !== 'PGRST116') {
        throw error;
      }

      return data;
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
        throw new Error('Impossible de se connecter à la base de données. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
      }
      
      console.error('Erreur JS lors de la récupération du ROI de la propriété:', error);
      throw error;
    }
  }
};

export default roiCalculatorService;